import React, { useEffect, useState } from 'react';
import ReactSlider from 'react-slider';
import { Label, Button, Input } from 'reactstrap';
import styled from 'styled-components';
import moment from 'moment';
import { Shift } from '../Shifts/types';
import { Employee, HandleShiftSave } from './ShiftScheduler';
import ActionIcon from '../../../components/custom/ActionIcon';
import { FormGroup } from 'reactstrap';
import Select from '../../../components/Select';
import useTypedForm from '../../../components/custom/hookForm/useTypedForm';
import useUser from '../../../customHooks/useUser';
import { getTsDate } from '../../../utils';
import CustomSwitch from '../../../components/custom/CustomSwitch';

const SHIFT_TYPES = ['SICK LEAVE', 'PTO', 'WORK'];

interface Props {
    handleShiftSave: (shift: Shift) => Promise<void>;
    deleteShift: (id: number) => Promise<void>;
    selectedTeamId: number;
    selectedDay: string;
    initialStartTime?: string;
    initialEndTime?: string;
    existingShifts: Array<Shift>;
    selectedEmployee: Employee;
    existingShiftId: number | undefined;
}


const ShiftTimePicker = ({
    handleShiftSave,
    deleteShift,
    selectedTeamId,
    initialStartTime,
    initialEndTime,
    selectedDay,
    existingShifts,
    selectedEmployee,
    existingShiftId
}: Props) => {

    const { user } = useUser();
    const isExistingShift = Boolean(existingShiftId);

    const days = [
        moment(selectedDay, 'YYYY-MM-DD').format('dddd MM/DD/YYYY'), // Day 1
        moment(selectedDay, 'YYYY-MM-DD').add(1, 'days').format('dddd MM/DD/YYYY'), // Day 2
    ];

    const totalIncrements = days.length * 48; // 2 days * 48 increments (30 minutes each)

    const {
        formState,
        getValues,
        setValue,
        reset,
        trigger,
        watch,
        handleSubmit,
        FormInput
    } = useTypedForm<Shift>();

    useEffect(() => {
        const now = getTsDate();

        if (isExistingShift) {
            let selectedShift;

            for (const key in selectedEmployee.shifts) {
                const shift = selectedEmployee.shifts[key];
                if (shift.id === existingShiftId) {
                    selectedShift = shift;
                }
            }

            if (!selectedShift) return;

            reset({
                ...selectedShift,
                modifiedBy: user.s_email,
                modifiedAt: now
            });
        } else {
            reset({
                email: selectedEmployee.s_email,
                type: 'WORK',
                createdBy: user.s_email,
                createdAt: now,
                modifiedBy: user.s_email,
                modifiedAt: now,
                teamId: selectedTeamId
            });
        }
        trigger();
    }, [isExistingShift, existingShifts, existingShiftId, selectedTeamId]);

    // Function to convert a full datetime string (YYYY-MM-DD HH:mm) into a slider increment
    const timeToIncrement = (dateTimeString) => {
        const datetime = moment.utc(dateTimeString);
        if (!datetime.isValid()) return NaN;

        // Calculate the number of increments from the start of the selected day
        const dayIndex = datetime.diff(moment(selectedDay, 'YYYY-MM-DD').startOf('day'), 'days');
        const hourIncrement = datetime.hour() * 2; // Each hour is 2 increments (half-hourly increments)
        const minuteIncrement = Math.floor(datetime.minute() / 30); // 0 or 1 depending on minutes

        // Calculate the total increment value
        return dayIndex * 48 + hourIncrement + minuteIncrement;
    };

    const [times, setTimes] = useState([
        timeToIncrement(initialStartTime),
        timeToIncrement(initialEndTime),
    ]);

    const [startDateTime, setStartDateTime] = useState('');
    const [endDateTime, setEndDateTime] = useState('');
    const [disabledRanges, setDisabledRanges] = useState([]);
    const [currentHours, setCurrentHours] = useState(0);
    const type = watch('type');
    const approved = watch('approved');

    useEffect(() => {
        updateDateTimeText(times);
        updateStartTimeEndTimeValues(times);
        updateTotalHours(times);
        // calculateDisabledRanges();
    }, [times, existingShifts]);

    const getDay = (index: number) => moment(selectedDay, 'YYYY-MM-DD').add(index, 'days');

    const getTimeValues = (times, dateFormat) => {
        const startHalfHour = times[0] % 48;
        const startDayIndex = Math.floor(times[0] / 48);
        const endHalfHour = times[1] % 48;
        const endDayIndex = Math.floor(times[1] / 48);

        const startTime = moment().startOf('day').add(startHalfHour * 30, 'minutes').format('HH:mm');
        const endTime = moment().startOf('day').add(endHalfHour * 30, 'minutes').format('HH:mm');
        const startDate = getDay(startDayIndex).format(dateFormat);
        const endDate = getDay(endDayIndex).format(dateFormat);

        return {
            startDate,
            endDate,
            startTime,
            endTime
        }
    }

    const updateDateTimeText = (times) => {
        const { startDate, startTime, endDate, endTime } = getTimeValues(times, 'dddd MM/DD/YYYY');
        setStartDateTime(`${startDate} ${startTime}`);
        setEndDateTime(`${endDate} ${endTime}`);
    };

    const updateStartTimeEndTimeValues = (times) => {
        const { startDate, startTime, endDate, endTime } = getTimeValues(times, 'YYYY-MM-DD');
        const startDateTime = `${startDate} ${startTime}`;
        const endDateTime = `${endDate} ${endTime}`;

        setValue('startDateTime', startDateTime);
        setValue('endDateTime', endDateTime);
    }

    // const calculateDisabledRanges = () => {
    //     const disabled = existingShifts.flatMap(shift => {
    //         // Exclude the current shift being edited
    //         if (shift.id === existingShiftId) {
    //             return []; // Skip this shift
    //         }

    //         const startMoment = moment.utc(shift.startDateTime);
    //         const endMoment = moment.utc(shift.endDateTime);

    //         const selectedDayStart = moment.utc(selectedDay, 'YYYY-MM-DD').startOf('day');
    //         const selectedDayEnd = moment.utc(selectedDay, 'YYYY-MM-DD').add(1, 'day').endOf('day'); // End of the next day

    //         // Check if the shift overlaps with the visible range
    //         const shiftOverlapsVisibleRange =
    //             endMoment.isAfter(selectedDayStart) && startMoment.isBefore(selectedDayEnd);

    //         if (shiftOverlapsVisibleRange) {
    //             const start = startMoment.isBefore(selectedDayStart)
    //                 ? 0
    //                 : timeToIncrement(shift.startDateTime);
    //             const end = endMoment.isAfter(selectedDayEnd)
    //                 ? totalIncrements
    //                 : timeToIncrement(shift.endDateTime);

    //             if (end < start) {
    //                 return [
    //                     { start, end: totalIncrements },
    //                     { start: 0, end },
    //                 ];
    //             } else {
    //                 return [{ start, end }];
    //             }
    //         }

    //         return [];
    //     });

    //     setDisabledRanges(disabled);
    // };


    const isCollision = (newStart, newEnd) => {
        return disabledRanges.some(range => {
            // Check if newStart or newEnd falls within any disabled range
            return (
                (newStart >= range.start && newStart < range.end) ||
                (newEnd > range.start && newEnd <= range.end) ||
                (newStart <= range.start && newEnd >= range.end)
            );
        });
    };


    const calculateTotalHours = (): number => {
        const totalHours = Object.values(selectedEmployee.shifts).reduce((total, shift) => {
            const currentShift = shift.id === existingShiftId;
            const startMoment = moment(shift.startDateTime);
            const endMoment = moment(shift.endDateTime);
            const add = currentShift ? 0 : endMoment.diff(startMoment, 'hours', true);
            return total + add; // Calculate hours and add to the total
        }, 0);
        return totalHours;
    };

    const isShiftOverlapping = (newShift: Shift, existingShifts: Shift[], existingShiftId?: number): boolean => {
        const newStart = moment.utc(newShift.startDateTime);
        const newEnd = moment.utc(newShift.endDateTime);

        if (!newStart.isValid() || !newEnd.isValid()) {
            // Invalid dates
            return false;
        }

        // Ensure newStart is before newEnd
        if (!newStart.isBefore(newEnd)) {
            // Invalid shift duration
            return true; // Treat invalid shift as overlapping to prevent saving
        }

        for (const shift of existingShifts) {
            // Exclude the current shift if editing
            if (existingShiftId && shift.id === existingShiftId) {
                continue;
            }

            const existingStart = moment.utc(shift.startDateTime);
            const existingEnd = moment.utc(shift.endDateTime);

            if (!existingStart.isValid() || !existingEnd.isValid()) {
                continue; // Skip invalid shifts
            }

            // Check for overlap
            if (newStart.isBefore(existingEnd) && newEnd.isAfter(existingStart)) {
                // Overlap detected
                return true;
            }
        }

        // No overlaps found
        return false;
    };

    const handleSliderChange = (newTimes) => {
        const [newStart, newEnd] = newTimes;

        // Convert slider increments to date-time strings in UTC
        const startDateTime = moment.utc(selectedDay, 'YYYY-MM-DD').add(newStart * 30, 'minutes').toISOString();
        const endDateTime = moment.utc(selectedDay, 'YYYY-MM-DD').add(newEnd * 30, 'minutes').toISOString();

        // Create a new shift object with the calculated start and end times
        const newShift: any = {
            startDateTime,
            endDateTime,
        };

        // Check for overlaps with existing shifts
        if (isShiftOverlapping(newShift, existingShifts, existingShiftId)) {
            console.log('Collision detected, preventing change');
            return; // Prevent the change
        }

        // If no collision, update the times and form values
        setTimes(newTimes); // Update the slider positions
        setValue('startDateTime', startDateTime);
        setValue('endDateTime', endDateTime);
    };

    // const handleTimeChange = (timeType: 'start' | 'end', value: string) => {
    //     const datetime = moment(value);
    //     const dayIndex = datetime.diff(moment(selectedDay, 'YYYY-MM-DD').startOf('day'), 'days');
    //     const halfHour = datetime.hour() * 2 + Math.floor(datetime.minute() / 30);

    //     const newTimes = timeType === 'start'
    //         ? [dayIndex * 48 + halfHour, times[1]]
    //         : [times[0], dayIndex * 48 + halfHour];

    //     // console.log(datetime);
    //     // console.log(disabledRanges);
    //     if (isCollision(newTimes[0], newTimes[1])) {
    //         return; // Prevent collision
    //     }
    //     setTimes(newTimes);
    // };

    const [disabledMarkers, setDisabledMarkers] = useState([]);

    useEffect(() => {
        const calculateDisabledMarkers = () => {
            // Initialize an array with 'totalIncrements' elements, all set to false
            const markers = Array(totalIncrements).fill(false);
        
            // Define the duration of each increment in minutes (30 minutes)
            const incrementDuration = 30;
        
            // Start of the visible range
            const visibleStart = moment.utc(selectedDay, 'YYYY-MM-DD').startOf('day');
        
            // Iterate over existing shifts
            existingShifts.forEach(shift => {
                // Exclude the current shift being edited
                if (shift.id === existingShiftId) {
                    return;
                }
        
                const existingStart = moment.utc(shift.startDateTime);
                const existingEnd = moment.utc(shift.endDateTime);
        
                if (!existingStart.isValid() || !existingEnd.isValid()) {
                    return; // Skip invalid shifts
                }
        
                // Ensure existingStart is before existingEnd
                if (!existingStart.isBefore(existingEnd)) {
                    return; // Skip invalid shifts
                }
        
                // Determine the overlap between the shift and the visible range
                const overlapStart = moment.max(existingStart, visibleStart);
                const overlapEnd = moment.min(existingEnd, visibleStart.clone().add(totalIncrements * incrementDuration, 'minutes'));
        
                if (overlapStart.isBefore(overlapEnd)) {
                    // Calculate start and end increments
                    const startIncrement = Math.floor(overlapStart.diff(visibleStart, 'minutes') / incrementDuration);
                    const endIncrement = Math.ceil(overlapEnd.diff(visibleStart, 'minutes') / incrementDuration);
        
                    // Mark the increments as disabled
                    for (let i = startIncrement; i < endIncrement; i++) {
                        if (i >= 0 && i < totalIncrements) {
                            markers[i] = true;
                        }
                    }
                }
            });
        
            return markers;
        };
        
        const markers = calculateDisabledMarkers();
        setDisabledMarkers(markers);
    }, [existingShifts, selectedDay]);

    useEffect(() => {
        const markers = calculateDisabledMarkers();
        setDisabledMarkers(markers);
    }, [existingShifts, selectedDay]);


    const calculateDisabledMarkers = () => {
        // Initialize an array of booleans representing each increment
        const markers = Array(totalIncrements).fill(false);
    
        existingShifts.forEach(shift => {
            // Exclude the current shift being edited
            if (shift.id === existingShiftId) {
                return;
            }
    
            const shiftStart = moment.utc(shift.startDateTime);
            const shiftEnd = moment.utc(shift.endDateTime);
    
            // Define the visible time range
            const visibleStart = moment.utc(selectedDay, 'YYYY-MM-DD').startOf('day');
            const visibleEnd = visibleStart.clone().add(days.length, 'days');
    
            // Check if the shift overlaps with the visible range
            if (shiftEnd.isAfter(visibleStart) && shiftStart.isBefore(visibleEnd)) {
                // Calculate increments for shift start and end times
                const shiftStartIncrement = Math.max(0, timeToIncrement(shiftStart));
                const shiftEndIncrement = Math.min(totalIncrements, timeToIncrement(shiftEnd));
    
                // Mark increments as disabled between shiftStartIncrement and shiftEndIncrement
                for (let i = shiftStartIncrement; i < shiftEndIncrement; i++) {
                    markers[i] = true;
                }
            }
        });
    
        return markers;
    };


    const calculateCurrentShiftHours = (newStart, newEnd): number => {
        const startMoment = moment(newStart, 'YYYY-MM-DDTHH:mm');
        const endMoment = moment(newEnd, 'YYYY-MM-DDTHH:mm');
        return endMoment.diff(startMoment, 'hours', true); // Calculate hours for the current shift
    };

    const updateTotalHours = (times) => {
        const [start, end] = times;
        // Calculate the current shift hours
        const currentShiftHours = calculateCurrentShiftHours(
            moment(selectedDay).startOf('day').add(start * 30, 'minutes').format('YYYY-MM-DDTHH:mm'),
            moment(selectedDay).startOf('day').add(end * 30, 'minutes').format('YYYY-MM-DDTHH:mm')
        );
        setCurrentHours(currentShiftHours);
    }

    const renderDayMarkers = () => {
        return Array.from({ length: totalIncrements }, (_, i) => {
            // Calculate the start and end time of the increment
            const incrementStart = moment.utc(selectedDay, 'YYYY-MM-DD').startOf('day').add(i * 30, 'minutes');
            const incrementEnd = incrementStart.clone().add(30, 'minutes');
    
            // Check if this increment overlaps with any existing shifts
            const isDisabled = existingShifts.some(shift => {
                // Exclude the current shift being edited
                if (shift.id === existingShiftId) {
                    return false;
                }
    
                const shiftStart = moment.utc(shift.startDateTime);
                const shiftEnd = moment.utc(shift.endDateTime);
    
                // Ensure valid shift times
                if (!shiftStart.isValid() || !shiftEnd.isValid() || !shiftStart.isBefore(shiftEnd)) {
                    return false; // Skip invalid shifts
                }
    
                // Check for overlap using the same logic as isShiftOverlapping
                return incrementStart.isBefore(shiftEnd) && incrementEnd.isAfter(shiftStart);
            });
    
            return (
                <Marker
                    key={i}
                    showBorder={i % 48 === 0}
                    isDisabled={isDisabled}
                />
            );
        });
    };
    
    const renderTrack = (props, state) => {
        const { index, start, end } = props;
        const isDisabled = disabledRanges.some(range =>
            (index >= range.start && index < range.end) ||
            (index < start && index >= end) // Wrap-around case
        );

        return (
            <Track
                {...props}
                index={state.index}
                start={times[0]}
                end={times[1]}
                isDisabled={isDisabled}
            />
        );
    };

    return (
        <div>
            <div className="text-center">
                <FlexContainer>
                    <DateTimeDisplay>
                        {startDateTime} to {endDateTime}.
                    </DateTimeDisplay>
                    <TotalHoursSpan>{currentHours} hours.</TotalHoursSpan>
                </FlexContainer>
                <div className="mb-3">
                    <ReactSlider
                        className="horizontal-slider"
                        thumbClassName="example-thumb"
                        trackClassName="example-track"
                        min={0}
                        max={totalIncrements} // 48 increments per day
                        value={times}
                        step={1}
                        onChange={handleSliderChange}
                        ariaLabel={['Start time', 'End time']}
                        ariaValuetext={(state) => `Time ${state.valueNow}`}
                        renderThumb={(props, state) => (
                            <div {...props}>
                                {moment().startOf('day').add(state.valueNow * 30, 'minutes').format('HH:mm')}
                            </div>
                        )}
                        renderTrack={renderTrack}
                    />
                    <DayMarkers>
                        {renderDayMarkers()}
                    </DayMarkers>
                    <DayHeaderContainer>
                        {days.map((day, index) => (
                            <DayHeader key={index} daysCount={days.length}>
                                {day}
                            </DayHeader>
                        ))}
                    </DayHeaderContainer>
                </div>
            </div>
            <FormGroup className={'text-left'}>
                <Label>Shift Type</Label>
                <Select
                    value={type}
                    options={SHIFT_TYPES.map((item) => ({ label: item, value: item }))}
                    onChange={option => setValue('type', String(option.value))}
                    width={300}
                />
            </FormGroup>
            <FormInput
                name={'notes'}
                label={'Notes'}
            />
            <CustomSwitch
                label={'Approved'}
                value={approved}
                handleSwitch={() => setValue('approved', !approved)}
            />
            <Footer isExistingShift={isExistingShift}>
                {
                    isExistingShift && (
                        <ActionIcon type={'delete'} onClick={() => deleteShift(existingShiftId)} />
                    )
                }
                <ActionIcon type={'save'} onClick={handleSubmit(handleShiftSave)} />
            </Footer>
        </div>
    );
};


const Track = styled.div<{ index: number; start: number; end: number; isDisabled: boolean }>`
    background: ${(props) =>
        props.isDisabled ? 'rgba(255, 0, 0, 0.5)' : props.index === 1 && props.start !== props.end ? 'rgb(5, 189, 5)' : '#ddd'};
    height: 10px;
    border-radius: 999px;
`;

const DateTimeDisplay = styled.div`
    margin-bottom: 20px;
    font-size: 18px;
    font-weight: bold;
`;

const TotalHoursSpan = styled(DateTimeDisplay)`
    width: 100px;
`;

const DayHeaderContainer = styled.div`
    display: flex;
    justify-content: space-between;
    margin-top: 10px;
`;

const DayHeader = styled.div<{ daysCount: number }>`
    width: calc(100% / ${(props) => props.daysCount});
    text-align: center;
    font-weight: bold;
`;

const DayMarkers = styled.div`
    display: flex;
    justify-content: space-between;
    margin-top: -10px; /* Adjust to align with the slider */
`;

const Marker = styled.div<{ showBorder: boolean; isDisabled: boolean }>`
    width: ${(props) => (props.showBorder ? '3px' : '1px')};
    height: ${(props) => (props.showBorder ? '15px' : '10px')};
    background-color: ${(props) => (props.isDisabled ? 'red' : props.showBorder ? 'black' : '#ccc')};
    /* padding: ${(props) => props.isDisabled ? '0px 5px' : '0px'}; */
`;

const TimeInputsContainer = styled.div`
    display: flex;
    justify-content: space-between;
    margin-top: 20px;
`;

const Footer = styled.div<{ isExistingShift: boolean }>`
    display: flex;
    justify-content: ${p => p.isExistingShift ? 'space-between' : 'flex-end'};
    padding: 0px; 
    margin: 0px;
    margin-top: 30px;
    padding-top: 20px;
    border-top: 1px solid #dee2e6;
    width: 100%;
`;

const FlexContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: baseline;
`;



export default ShiftTimePicker;


{/* <TimeInputsContainer>
                <div>
                    <Label>Start Time</Label>
                    <Input
                        type="datetime-local"
                        value={moment(times[0] * 30 * 60 * 1000 + moment(selectedDay, 'YYYY-MM-DD').startOf('day').valueOf()).format(
                            'YYYY-MM-DDTHH:mm'
                        )}
                        onChange={(e) => handleTimeChange('start', e.target.value)}
                    />
                </div>
                <div>
                    <Label>End Time</Label>
                    <Input
                        type="datetime-local"
                        value={moment(times[1] * 30 * 60 * 1000 + moment(selectedDay, 'YYYY-MM-DD').startOf('day').valueOf()).format(
                            'YYYY-MM-DDTHH:mm'
                        )}
                        onChange={(e) => handleTimeChange('end', e.target.value)}
                    />
                </div>
            </TimeInputsContainer> */}